import { useMemo } from 'react'
import { Position, type Edge } from '@xyflow/react'
import type { AdGenStageStatus } from '@/services/ad-generator/types'
import type { PipelineNode, PipelineNodeData, PipelineStage } from '../types'
import { STAGE_CONNECTIONS, getStageDefinition } from '../utils/stageDefinitions'

export function usePipelineNodes(
  stages: AdGenStageStatus[],
  currentStage: string,
  onStageClick?: (stageName: string) => void
): { nodes: PipelineNode[]; edges: Edge[] } {
  return useMemo(() => {
    // Create nodes from stages
    const nodes: PipelineNode[] = stages.map((stage) => {
      const definition = getStageDefinition(stage.name)

      const pipelineStage: PipelineStage = {
        id: stage.name,
        name: stage.name,
        displayName: stage.display_name,
        status: stage.status,
        etaSeconds: stage.eta_seconds,
        durationMs: stage.duration_ms,
        insight: stage.insight,
      }

      const data: PipelineNodeData = {
        stage: pipelineStage,
        isActive: stage.name === currentStage,
        onClick: onStageClick ? () => onStageClick(stage.name) : undefined,
      }

      return {
        id: stage.name,
        type: 'pipelineNode',
        position: { x: 0, y: 0 }, // Will be set by layout
        data,
        // Use stage order from definitions for positioning
        ...(definition && {
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
        }),
      }
    })

    // Create edges from connections
    const edges: Edge[] = STAGE_CONNECTIONS
      .filter(([source, target]) => {
        // Only include edges where both nodes exist
        return nodes.some(n => n.id === source) && nodes.some(n => n.id === target)
      })
      .map(([source, target]) => {
        const sourceStage = stages.find(s => s.name === source)
        const targetStage = stages.find(s => s.name === target)

        // Determine edge state based on source and target status
        const isSourceCompleted = sourceStage?.status === 'completed' || sourceStage?.status === 'degraded'
        const isTargetActive = targetStage?.status === 'running'

        return {
          id: `${source}-${target}`,
          source,
          target,
          type: 'pipelineEdge',
          animated: isTargetActive,
          data: {
            isCompleted: isSourceCompleted && !isTargetActive,
            isActive: isTargetActive,
          },
        }
      })

    return { nodes, edges }
  }, [stages, currentStage, onStageClick])
}
